import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { _getWalletRecipients } from '@/api/address-book-bff/queries';
import type { WalletRecipientView } from '@/api/address-book-bff/types';
import { type ResolvedRecipient, resolveRecipient } from '@/api/wallet/queries';
import useDebounce from '@/global-hooks/use-debounce';
import {
  classifyRecipient,
  getNormalizedSearchLength,
  getNormalizedSuiAddress,
} from '@/shared/modules/wallet/send/recipient';
import { isValidEmail } from '@/utils/validate-email';

const SEARCH_DEBOUNCE_MS = 300;
const RECIPIENT_RESOLUTION_QUERY_KEY = 'wallet-recipient-resolution';

export interface SendRecipientSearchResult {
  recipients: WalletRecipientView[];
  isSearching: boolean;
  showEmpty: boolean;
  showSearchError: boolean;
  addressMatch: WalletRecipientView | null;
}

export type RecipientResolvedHandler = (_recipient: ResolvedRecipient) => void;

type RecipientSearchInput = {
  recipientKind: ReturnType<typeof classifyRecipient>;
  normalizedAddress: string | null;
  normalizedEmail: string | null;
  contactQueryInput: string;
  isSearchEligible: boolean;
  isCurrentSearch: boolean;
};

function getRecipientSearchInput(input: string, debouncedInput: string): RecipientSearchInput {
  const trimmedInput = input.trim();
  const recipientKind = classifyRecipient(input);
  const normalizedAddress = recipientKind === 'address' ? getNormalizedSuiAddress(input) : null;
  const isSearchEligible = recipientKind === 'search' && getNormalizedSearchLength(input) >= 2;
  const isCurrentSearch = isSearchEligible && debouncedInput === trimmedInput;
  const normalizedEmail = isSearchEligible && isValidEmail(trimmedInput) ? trimmedInput : null;

  return {
    recipientKind,
    normalizedAddress,
    normalizedEmail,
    contactQueryInput: normalizedAddress ?? trimmedInput,
    isSearchEligible,
    isCurrentSearch,
  };
}

function useRecipientEmailResolution(
  email: string | null,
  isCurrentSearch: boolean,
  onResolved?: RecipientResolvedHandler,
): boolean {
  const resolutionQuery = useQuery({
    queryKey: [RECIPIENT_RESOLUTION_QUERY_KEY, email],
    queryFn: () => resolveRecipient(email ?? ''),
    enabled: email !== null && isCurrentSearch,
    retry: false,
  });
  const lastResolvedEmail = useRef<string | null>(null);

  useEffect(() => {
    if (!email || !resolutionQuery.data || lastResolvedEmail.current === email) return;

    lastResolvedEmail.current = email;
    onResolved?.(resolutionQuery.data);
  }, [email, onResolved, resolutionQuery.data]);

  useEffect(() => {
    if (!email) lastResolvedEmail.current = null;
  }, [email]);

  return resolutionQuery.isFetching;
}

export function useSendRecipientSearch(
  input: string,
  onResolved?: RecipientResolvedHandler,
): SendRecipientSearchResult {
  const debouncedInput = useDebounce(input, SEARCH_DEBOUNCE_MS).trim();
  const searchInput = getRecipientSearchInput(input, debouncedInput);
  const recipientsQuery = useQuery({
    ..._getWalletRecipients(searchInput.contactQueryInput),
    enabled: searchInput.recipientKind === 'address' || searchInput.isCurrentSearch,
  });
  const isResolvingRecipient = useRecipientEmailResolution(
    searchInput.normalizedEmail,
    searchInput.isCurrentSearch,
    onResolved,
  );
  const recipients =
    searchInput.isCurrentSearch && recipientsQuery.isSuccess ? recipientsQuery.data.recipients : [];
  const addressMatch =
    searchInput.recipientKind === 'address' && recipientsQuery.isSuccess
      ? (recipientsQuery.data.recipients[0] ?? null)
      : null;

  return {
    recipients,
    isSearching:
      searchInput.recipientKind === 'address'
        ? recipientsQuery.isFetching
        : searchInput.isSearchEligible &&
          (!searchInput.isCurrentSearch || recipientsQuery.isFetching || isResolvingRecipient),
    showEmpty: searchInput.isCurrentSearch && recipientsQuery.isSuccess && recipients.length === 0,
    showSearchError: searchInput.isCurrentSearch && recipientsQuery.isError,
    addressMatch,
  };
}
